import { memo, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import s from './TaggableComposer.module.css';
import emojiAssetModules from './customEmojiAssets.js';

const EMOJI_META_OVERRIDES = {
  campfire: { emoji: '🔥', label: 'Campfire', keywords: ['campfire', 'warmth', 'gather'] },
  log: { emoji: '🪵', label: 'Log', keywords: ['log', 'wood', 'campfire'] },
  heart_flame: { emoji: '❤️‍🔥', label: 'Heart flame', keywords: ['heart', 'passion', 'love'] },
  seedling: { emoji: '🌱', label: 'Seedling', keywords: ['growth', 'seedling', 'hope'] },
  star: { emoji: '⭐', label: 'Star', keywords: ['star', 'light', 'shine'] },
  galaxy: { emoji: '🌌', label: 'Galaxy', keywords: ['galaxy', 'cosmos', 'dream'] },
  dove_flame: { emoji: '🕊️', label: 'Dove', keywords: ['peace', 'dove', 'spirit'] },
};

const titleize = (input = '') =>
  input
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const buildEmojiItems = () => {
  const entries = Object.entries(emojiAssetModules);
  const items = entries.map(([path, assetUrl]) => {
    const match = path.match(/([\w-]+)\.png$/i);
    const key = match ? match[1] : path;
    const override = EMOJI_META_OVERRIDES[key] || {};
    const label = override.label || titleize(key);
    const keywords = Array.from(
      new Set([
        ...(override.keywords || []),
        key,
        key.replace(/_/g, ' '),
        label,
        label.toLowerCase(),
      ].filter(Boolean)),
    );

    return {
      key,
      emoji: override.emoji || override.fallback || label,
      label,
      keywords,
      assetUrl,
    };
  });

  return items.sort((a, b) => a.label.localeCompare(b.label));
};

const CUSTOM_EMOJI_GROUPS = [
  {
    id: 'sparks',
    icon: '✨',
    label: 'Sparks',
    items: buildEmojiItems(),
  },
];

const ALL_EMOJIS = CUSTOM_EMOJI_GROUPS.flatMap((group) => group.items);

const TaggableComposerEmojiPicker = memo(function TaggableComposerEmojiPicker({ open, onSelect }) {
  const intl = useIntl();
  const [activeGroup, setActiveGroup] = useState(CUSTOM_EMOJI_GROUPS[0]?.id);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveGroup(CUSTOM_EMOJI_GROUPS[0]?.id);
    }
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();
  const isSearching = normalizedQuery.length > 0;

  const visibleEmojis = useMemo(() => {
    if (isSearching) {
      return ALL_EMOJIS.filter((item) => {
        if (!item) return false;
        const label = item.label || '';
        const matchLabel = label.toLowerCase().includes(normalizedQuery);
        const tags = Array.isArray(item.keywords) ? item.keywords : [];
        const matchTags = tags.some((tag) => tag && tag.toLowerCase().includes(normalizedQuery));
        return matchLabel || matchTags;
      });
    }
    const group = CUSTOM_EMOJI_GROUPS.find((entry) => entry.id === activeGroup);
    return group?.items || [];
  }, [activeGroup, isSearching, normalizedQuery]);

  const handleSelect = (item) => {
    if (!item) return;

    if (item.key) {
      onSelect?.({
        key: item.key,
        emoji: item.emoji,
        label: item.label,
        assetUrl: item.assetUrl,
        keywords: Array.isArray(item.keywords) ? item.keywords : [],
      });
      return;
    }

    if (item.emoji && typeof item.emoji === 'string') {
      onSelect?.({
        emoji: item.emoji,
        label: item.label,
        keywords: Array.isArray(item.keywords) ? item.keywords : [],
      });
    }
  };

  return (
    <div className={s.emojiPopover}>
      <div className={s.emojiSearchRow}>
        <input
          type="search"
          className={s.emojiSearchInput}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={intl.formatMessage({
            id: 'composer_search_emoji',
            defaultMessage: 'Search emojis',
          })}
        />
      </div>
      <div className={s.emojiCategoryList}>
        {CUSTOM_EMOJI_GROUPS.map((group) => {
          const icon = group.icon || '✨';
          const label = group.label || icon;
          const isActive = !isSearching && activeGroup === group.id;
          return (
            <button
              key={group.id}
              type="button"
              className={isActive ? `${s.emojiCategoryButton} ${s.emojiCategoryButtonActive}` : s.emojiCategoryButton}
              onClick={() => {
                setActiveGroup(group.id);
                setQuery('');
              }}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              title={label}
              aria-label={label}
            >
              <span aria-hidden>{icon}</span>
            </button>
          );
        })}
      </div>
      <div className={s.emojiGrid}>
        {visibleEmojis.map((item) => {
          const buttonKey = item.key || item.emoji || item.label;
          const hasAsset = Boolean(item.key);
          return (
            <button
              key={buttonKey}
              type="button"
              className={s.emojiItem}
              onClick={() => handleSelect(item)}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              aria-label={item.label || item.emoji}
            >
              {hasAsset ? (
                <img
                  src={item.assetUrl}
                  alt=""
                  width={32}
                  height={32}
                  aria-hidden={true}
                />
              ) : (
                <span aria-hidden>{item.emoji}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default TaggableComposerEmojiPicker;
